import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { voidedDocApi } from "./axios";
// AJUSTAR: crear voidedDocApi en ./axios.ts siguiendo el mismo patrón que
// emittedDocApi/retentionDocApi (misma instancia de axios, baseURL apuntando
// a /api/stk-voided-document).
import {
  DataI,
  ServiceResponseI,
  PropsVoidedDocumentI,
  VoidedDocumentI,
} from "../../interfaces";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";

const getVoidedDocuments = async ({
  pagination,
  sorting,
  globalFilter,
  dateFrom,
  dateTo,
  dateField,
  codDoc,
  excludeCodDoc,
}: PropsVoidedDocumentI): Promise<ServiceResponseI<DataI<VoidedDocumentI>>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page", (pagination!.pageIndex + 1).toString());
    params.append("limit", pagination!.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    params.append("dateField", dateField ?? "");
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo) params.append("dateTo", dateTo);
    if (codDoc) params.append("codDoc", codDoc);
    if (excludeCodDoc) params.append("excludeCodDoc", excludeCodDoc);

    const { data } = await voidedDocApi.get<
      ServiceResponseI<DataI<VoidedDocumentI>>
    >("", {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const deleteVoidedDocument = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await voidedDocApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      {
        headers: { Authorization: validateToken },
      },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

/**
 * Sincroniza "Comprobantes anulados" del SRI para un rango de meses —
 * registra en external_voided_document y marca ANULADO cualquier
 * emitted_document existente que coincida por clave de acceso. Mismo
 * mecanismo de sesión reutilizada/login que el resto de los syncs del SRI.
 */
export const syncVoidedRango = async (payload: {
  mesDesde: string;
  mesHasta: string;
}) => {
  const t = ValidateCurrentToken();
  if (!t) throw new Error("TOKEN INVALIDO");
  const { data } = await voidedDocApi.post("/sync", payload, {
    headers: { Authorization: t },
    timeout: 900_000,
  });
  return data;
};

export const useVoidedDocuments = ({
  sorting,
  pagination,
  globalFilter,
  dateFrom,
  dateTo,
  dateField,
  codDoc,
  excludeCodDoc,
}: PropsVoidedDocumentI) => {
  const queryClient = useQueryClient();

  const voidedDocumentQuery = useQuery(
    [
      "voided-documents",
      { pagination, sorting, globalFilter, dateFrom, dateTo, dateField, codDoc, excludeCodDoc },
    ],
    () =>
      getVoidedDocuments({
        pagination,
        sorting,
        globalFilter,
        dateFrom,
        dateTo,
        dateField,
        codDoc,
        excludeCodDoc,
      }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  const deleteOneMutation = useMutation(
    (id: string) => deleteVoidedDocument(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["voided-documents"]);
      },
    },
  );

  const deleteBulkMutation = useMutation(
    (ids: string[]) => Promise.all(ids.map((id) => deleteVoidedDocument(id))),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["voided-documents"]);
      },
    },
  );

  // Sync manual para un rango de meses elegido desde el modal. Al
  // terminar, invalida esta query Y la de emitidos (por si marcó algo
  // ANULADO ahí, para que ese panel también se refresque).
  const syncVoidedMutation = useMutation(
    (payload: { mesDesde: string; mesHasta: string }) =>
      syncVoidedRango(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["voided-documents"]);
        queryClient.invalidateQueries(["emitted-documents"]);
      },
    },
  );

  return {
    voidedDocumentQuery,
    deleteOneMutation,
    deleteBulkMutation,
    syncVoidedMutation,
  };
};